<template>
  <div class="pa-4">
    <v-alert-dialog ref="alert" />

    <!-- Layout Master / Detail -->
    <v-row>
      <!-- MASTER: Tipos -->
      <v-col cols="12" md="5">
        <v-card outlined class="rounded-lg card-master">
          <v-card-title class="pb-2">
            <v-icon class="mr-2" color="primary">mdi-folder-multiple</v-icon>
            Tipos
            <v-spacer />

            <v-btn color="primary" small @click="openDialogTipo(null)">
              <v-icon left small>mdi-plus</v-icon>
              Nuevo Tipo
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-0">
            <v-text-field
              v-model="searchTipo"
              dense
              outlined
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
              label="Buscar tipo..."
              class="mb-3"
            />

            <v-skeleton-loader
              v-if="loading"
              type="list-item-avatar-two-line, list-item-avatar-two-line, list-item-avatar-two-line"
            />

            <v-list v-else dense class="py-0">
              <template v-if="filteredTipos.length > 0">
                <v-list-item
                  v-for="t in filteredTipos"
                  :key="t.id_gasto_tipo"
                  @click="selectTipo(t)"
                  :class="{
                    'selected-row': selectedTipo && selectedTipo.id_gasto_tipo === t.id_gasto_tipo
                  }"
                >
                  <v-list-item-avatar>
                    <v-icon color="primary">mdi-folder</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ t.nombre }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ (t.subtipos ? t.subtipos.length : 0) }} subclasificación(es)
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon v-bind="attrs" v-on="on">
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>

                      <v-list dense>
                        <v-list-item @click.stop="openDialogTipo(t)">
                          <v-list-item-icon>
                            <v-icon>mdi-pencil</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Editar</v-list-item-title>
                        </v-list-item>

                        <v-list-item @click.stop="openDialogDeleteTipo(t)">
                          <v-list-item-icon>
                            <v-icon>mdi-delete</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Eliminar</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-list-item-action>
                </v-list-item>
              </template>

              <v-alert v-else type="info" outlined dense class="mb-0">
                No hay tipos disponibles.
              </v-alert>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- DETAIL: Subtipos del tipo seleccionado -->
      <v-col cols="12" md="7">
        <v-card outlined class="rounded-lg" height="100%">
          <v-card-title class="pb-2">
            <div v-if="selectedTipo" class="text-h6 font-weight-bold d-flex align-center">
              <v-icon class="mr-2" color="indigo">mdi-tag-multiple</v-icon>
              {{ selectedTipo.nombre }}
            </div>
            <div v-else class="text-h6 font-weight-bold d-flex align-center">
              <v-icon class="mr-2" color="grey">mdi-information</v-icon>
              Subclasificaciones
            </div>

            <v-spacer />

            <v-chip
              v-if="selectedTipo"
              small
              outlined
              color="primary"
              class="ml-2"
            >
              {{ currentSubtipos.length }} subclasificación(es)
            </v-chip>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <v-alert v-if="!selectedTipo" type="info" outlined dense>
              Selecciona un tipo de gasto para ver y administrar sus subclasificaciones.
            </v-alert>

            <template v-else>
              <div class="d-flex align-center mb-3">
                <v-spacer />

                <v-btn
                  color="primary"
                  small
                  @click="openDialogSubtipo(null, selectedTipo.id_gasto_tipo)"
                >
                  <v-icon left small>mdi-plus</v-icon>
                  Agregar Subclasificación
                </v-btn>
              </div>

              <v-text-field
                v-model="searchSubtipo"
                dense
                outlined
                clearable
                hide-details
                prepend-inner-icon="mdi-magnify"
                label="Buscar subclasificación..."
                class="mb-3"
              />

              <template v-if="filteredSubtipos.length > 0">
                <v-list dense class="py-0">
                  <v-list-item
                    v-for="s in filteredSubtipos"
                    :key="s.id_gasto_subtipo"
                    class="subtipo-row"
                  >
                    <v-list-item-avatar>
                      <v-icon color="indigo">mdi-tag</v-icon>
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">{{ s.nombre }}</v-list-item-title>

                      <!-- mini-badges para diferenciar configuraciones -->
                      <div class="mt-1 d-flex flex-wrap" style="gap:6px">
                        <v-chip x-small outlined color="blue">
                          <v-icon x-small left>mdi-file-document-outline</v-icon>
                          OPP: {{ countArr(s.programacion_pago_orden_requisitos) }}/{{ countArr(s.programacion_pago_orden_opcionales) }}
                        </v-chip>
                        <v-chip x-small outlined color="green">
                          <v-icon x-small left>mdi-cash-check</v-icon>
                          SP: {{ countArr(s.solicitud_pago_requisitos) }}/{{ countArr(s.solicitud_pago_opcionales) }}
                        </v-chip>
                      </div>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-btn icon @click="openDialogSubtipo(s)">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </v-list-item-action>

                    <v-list-item-action>
                      <v-btn icon @click="openDialogDeleteSubtipo(s)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </template>

              <v-alert v-else type="info" outlined dense class="mb-0">
                No hay subclasificaciones para este tipo (o no coinciden con tu búsqueda).
              </v-alert>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog: Tipo -->
    <v-dialog max-width="520" v-model="dialogTipo" persistent>
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-shape</v-icon>
          {{ editedTipo.id_gasto_tipo === null ? "Crear" : "Editar" }} Tipo de Gasto
        </v-card-title>

        <v-card-text>
          <v-text-field
            v-model="editedTipo.nombre"
            label="Nombre del Tipo de Gasto"
            outlined
            dense
            counter="256"
            :rules="rulesNombre"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text color="green" @click="closeDialogTipo">Cancelar</v-btn>
          <v-btn text color="red" @click="saveTipo" :loading="loadingSaveTipo">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Subtipo -->
    <v-dialog max-width="820" v-model="dialogSubtipo" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-tag</v-icon>
          {{ editedSubtipo.id_gasto_subtipo === null ? "Crear" : "Editar" }} Subclasificación
          <v-spacer />
          <v-chip
            v-if="editedSubtipo.id_gasto_subtipo !== null"
            small
            outlined
            color="grey"
          >
            Modo edición
          </v-chip>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" md="5">
              <v-select
                v-model="editedSubtipo.id_gasto_tipo"
                :items="items"
                item-text="nombre"
                item-value="id_gasto_tipo"
                label="Tipo de Gasto"
                outlined
                dense
                :disabled="editedSubtipo.id_gasto_subtipo !== null"
              />
            </v-col>

            <v-col cols="12" md="7">
              <v-text-field
                v-model="editedSubtipo.nombre"
                label="Nombre de la Subclasificación"
                outlined
                dense
                counter="256"
                :rules="rulesNombre"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <div class="section-card oc">
                <div class="section-title">
                  <v-icon left color="blue">mdi-file-document-outline</v-icon>
                  Órdenes de Programación de Pago
                </div>

                <v-autocomplete
                  v-model="editedSubtipo.programacion_pago_orden_requisitos"
                  label="Campos obligatorios"
                  outlined
                  dense
                  chips
                  multiple
                  clearable
                  :items="requerimientosOCRequisitos"
                  item-text="text"
                  item-value="value"
                  :menu-props="{ maxHeight: '260' }"
                />

                <v-autocomplete
                  v-model="editedSubtipo.programacion_pago_orden_opcionales"
                  label="Campos opcionales"
                  outlined
                  dense
                  chips
                  multiple
                  clearable
                  :items="requerimientosOCOpcionales"
                  item-text="text"
                  item-value="value"
                  :menu-props="{ maxHeight: '260' }"
                />

              </div>
            </v-col>

            <!-- SOLICITUD DE PAGO -->
            <v-col cols="12" md="6">
              <div class="section-card sp">
                <div class="section-title">
                  <v-icon left color="green">mdi-cash-check</v-icon>
                  Solicitudes de Pago
                </div>

                <v-autocomplete
                  v-model="editedSubtipo.solicitud_pago_requisitos"
                  label="Campos obligatorios"
                  outlined
                  dense
                  chips
                  multiple
                  clearable
                  :items="requerimientosSPRequisitos"
                  item-text="text"
                  item-value="value"
                  :menu-props="{ maxHeight: '260' }"
                />

                <v-autocomplete
                  v-model="editedSubtipo.solicitud_pago_opcionales"
                  label="Campos opcionales"
                  outlined
                  dense
                  chips
                  multiple
                  clearable
                  :items="requerimientosSPOpcionales"
                  item-text="text"
                  item-value="value"
                  :menu-props="{ maxHeight: '260' }"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text color="green" @click="closeDialogSubtipo">Cancelar</v-btn>
          <v-btn text color="red" @click="saveSubtipo" :loading="loadingSaveSubtipo">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Eliminar Tipo -->
    <v-dialog max-width="520" v-model="dialogDeleteTipo" persistent>
      <v-card>
        <v-card-title class="red--text">
          <v-icon color="red" class="mr-2">mdi-alert</v-icon>
          Eliminar Tipo de Gasto
        </v-card-title>

        <v-card-text>
          ¿Seguro que deseas eliminar <strong>"{{ editedTipo.nombre }}"</strong>?
          <div class="mt-2 text--secondary">
            Se eliminarán también todas sus subclasificaciones. Esta acción no se puede revertir.
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text color="green" @click="closeDialogDeleteTipo">Cancelar</v-btn>
          <v-btn text color="red" @click="deleteTipo" :loading="loadingDeleteTipo">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Eliminar Subtipo -->
    <v-dialog max-width="520" v-model="dialogDeleteSubtipo" persistent>
      <v-card>
        <v-card-title class="red--text">
          <v-icon color="red" class="mr-2">mdi-alert</v-icon>
          Eliminar Subclasificación
        </v-card-title>

        <v-card-text>
          ¿Seguro que deseas eliminar <strong>"{{ editedSubtipo.nombre }}"</strong>?
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text color="green" @click="closeDialogDeleteSubtipo">Cancelar</v-btn>
          <v-btn text color="red" @click="deleteSubtipo" :loading="loadingDeleteSubtipo">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import VAlertDialog from "@/components/VAlertDialog.vue";

export default {
  components: { VAlertDialog },
  data: () => ({
    loading: false,
    items: [],
    selectedTipo: null,

    searchTipo: "",
    searchSubtipo: "",

    // dialogs tipo
    dialogTipo: false,
    editedTipo: { id_gasto_tipo: null, nombre: "" },
    defaultEditedTipo: { id_gasto_tipo: null, nombre: "" },
    loadingSaveTipo: false,
    dialogDeleteTipo: false,
    loadingDeleteTipo: false,

    // dialogs subtipo
    dialogSubtipo: false,
    editedSubtipo: {
      id_gasto_subtipo: null,
      id_gasto_tipo: null,
      nombre: "",
      programacion_pago_orden_requisitos: [],
      programacion_pago_orden_opcionales: [],
      solicitud_pago_requisitos: [],
      solicitud_pago_opcionales: []
    },
    defaultEditedSubtipo: {
      id_gasto_subtipo: null,
      id_gasto_tipo: null,
      nombre: "",
      programacion_pago_orden_requisitos: [],
      programacion_pago_orden_opcionales: [],
      solicitud_pago_requisitos: [],
      solicitud_pago_opcionales: []
    },
    loadingSaveSubtipo: false,
    dialogDeleteSubtipo: false,
    loadingDeleteSubtipo: false,

    requerimientos: [
      { value: 1, text: "No. de Serie" },
      { value: 2, text: "Marca" },
      { value: 3, text: "Modelo" },
      { value: 4, text: "No. Inventario UBGA" },
      { value: 5, text: "Proyecto" },
      { value: 6, text: "Empleado(s)" }
    ],

    rulesNombre: [
      v => (!!v && v.trim().length >= 1) || "Requerido",
      v => (!v || v.length <= 256) || "Máximo 256 caracteres"
    ]
  }),
  computed: {
    filteredTipos() {
      const q = (this.searchTipo || "").toLowerCase().trim();
      if (!q) return this.items;
      return this.items.filter(x => (x.nombre || "").toLowerCase().includes(q));
    },
    currentSubtipos() {
      if (!this.selectedTipo) return [];
      return this.selectedTipo.subtipos || [];
    },
    filteredSubtipos() {
      const list = this.currentSubtipos;
      const q = (this.searchSubtipo || "").toLowerCase().trim();
      if (!q) return list;
      return list.filter(x => (x.nombre || "").toLowerCase().includes(q));
    },
    requerimientosOCRequisitos() {
      // Requisitos OC: todo menos lo que esté en opcionales OC
      return this.filterOut(this.requerimientos, this.editedSubtipo.programacion_pago_orden_opcionales);
    },
    requerimientosOCOpcionales() {
      // Opcionales OC: todo menos lo que esté en requisitos OC
      return this.filterOut(this.requerimientos, this.editedSubtipo.programacion_pago_orden_requisitos);
    },

    requerimientosSPRequisitos() {
      // Requisitos SP: todo menos lo que esté en opcionales SP
      return this.filterOut(this.requerimientos, this.editedSubtipo.solicitud_pago_opcionales);
    },
    requerimientosSPOpcionales() {
      // Opcionales SP: todo menos lo que esté en requisitos SP
      return this.filterOut(this.requerimientos, this.editedSubtipo.solicitud_pago_requisitos);
    }
  },
  mounted() {
    this.getItems();
  },
  methods: {
    // helper para mini-badges
    countArr(arr) {
      return Array.isArray(arr) ? arr.length : 0;
    },

    getItems() {
      this.loading = true;
      axios
        .get("/Pagos/ListarGastoTipos")
        .then(res => {
          this.items = res.data || [];

          if (this.selectedTipo) {
            const found = this.items.find(
              x => x.id_gasto_tipo === this.selectedTipo.id_gasto_tipo
            );
            this.selectedTipo = found || null;
          }

          if (!this.selectedTipo && this.items.length > 0) {
            this.selectedTipo = this.items[0];
          }
        })
        .catch(err => console.log(err))
        .finally(() => (this.loading = false));
    },

    selectTipo(t) {
      this.selectedTipo = t;
      this.searchSubtipo = "";
    },

    // ===== Tipo =====
    openDialogTipo(editItem = null) {
      this.editedTipo = editItem
        ? Object.assign({}, editItem)
        : Object.assign({}, this.defaultEditedTipo);
      this.dialogTipo = true;
    },
    closeDialogTipo() {
      this.editedTipo = Object.assign({}, this.defaultEditedTipo);
      this.dialogTipo = false;
    },
    saveTipo() {
      const nombre = (this.editedTipo.nombre || "").trim();
      if (!nombre || nombre.length > 256) {
        this.$refs.alert.setMessage("", "Debe ingresar un nombre entre 1 y 256 caracteres");
        this.$refs.alert.open();
        return;
      }

      this.loadingSaveTipo = true;

      axios({
        method: this.editedTipo.id_gasto_tipo === null ? "POST" : "PUT",
        url:
          this.editedTipo.id_gasto_tipo === null
            ? "/Pagos/CrearGastoTipo"
            : `/Pagos/EditarGastoTipo/${this.editedTipo.id_gasto_tipo}`,
        data: { nombre }
      })
        .then(() => {
          this.closeDialogTipo();
          this.getItems();
        })
        .catch(error => {
          if (error.response && error.response.status === 409) {
            this.$refs.alert.setMessage("", "Ya existe un tipo de gasto con ese nombre.");
            this.$refs.alert.open();
            return;
          }
          console.log(error);
        })
        .finally(() => (this.loadingSaveTipo = false));
    },
    openDialogDeleteTipo(editItem) {
      this.editedTipo = Object.assign({}, editItem);
      this.dialogDeleteTipo = true;
    },
    closeDialogDeleteTipo() {
      this.editedTipo = Object.assign({}, this.defaultEditedTipo);
      this.dialogDeleteTipo = false;
    },
    deleteTipo() {
      this.loadingDeleteTipo = true;
      axios
        .delete(`/Pagos/EliminarGastoTipo/${this.editedTipo.id_gasto_tipo}`)
        .then(() => {
          if (
            this.selectedTipo &&
            this.selectedTipo.id_gasto_tipo === this.editedTipo.id_gasto_tipo
          ) {
            this.selectedTipo = null;
          }
          this.closeDialogDeleteTipo();
          this.getItems();
        })
        .catch(err => console.log(err))
        .finally(() => (this.loadingDeleteTipo = false));
    },

    // ===== Subtipo =====
    openDialogSubtipo(editItem = null, idGastoTipo = null) {
      if (editItem) {
        this.editedSubtipo = Object.assign({}, this.defaultEditedSubtipo, editItem);

        // normalizar arrays por si vienen null
        this.editedSubtipo.programacion_pago_orden_requisitos = Array.isArray(this.editedSubtipo.programacion_pago_orden_requisitos)
          ? this.editedSubtipo.programacion_pago_orden_requisitos
          : [];
        this.editedSubtipo.programacion_pago_orden_opcionales = Array.isArray(this.editedSubtipo.programacion_pago_orden_opcionales)
          ? this.editedSubtipo.programacion_pago_orden_opcionales
          : [];
        this.editedSubtipo.solicitud_pago_requisitos = Array.isArray(this.editedSubtipo.solicitud_pago_requisitos)
          ? this.editedSubtipo.solicitud_pago_requisitos
          : [];
        this.editedSubtipo.solicitud_pago_opcionales = Array.isArray(this.editedSubtipo.solicitud_pago_opcionales)
          ? this.editedSubtipo.solicitud_pago_opcionales
          : [];
      } else {
        this.editedSubtipo = Object.assign({}, this.defaultEditedSubtipo);
        this.editedSubtipo.id_gasto_tipo = idGastoTipo;
      }
      this.dialogSubtipo = true;
    },
    closeDialogSubtipo() {
      this.editedSubtipo = Object.assign({}, this.defaultEditedSubtipo);
      this.dialogSubtipo = false;
    },
    saveSubtipo() {
      if (!this.editedSubtipo.id_gasto_tipo) {
        this.$refs.alert.setMessage("", "Debe seleccionar un tipo de gasto");
        this.$refs.alert.open();
        return;
      }

      const nombre = (this.editedSubtipo.nombre || "").trim();
      if (!nombre || nombre.length > 256) {
        this.$refs.alert.setMessage("", "Debe ingresar un nombre entre 1 y 256 caracteres");
        this.$refs.alert.open();
        return;
      }

      this.loadingSaveSubtipo = true;

      axios({
        method: this.editedSubtipo.id_gasto_subtipo === null ? "POST" : "PUT",
        url:
          this.editedSubtipo.id_gasto_subtipo === null
            ? "/Pagos/CrearGastoSubtipo"
            : `/Pagos/EditarGastoSubtipo/${this.editedSubtipo.id_gasto_subtipo}`,
        data: {
          id_gasto_tipo: this.editedSubtipo.id_gasto_tipo,
          nombre,
          programacion_pago_orden_requisitos: this.editedSubtipo.programacion_pago_orden_requisitos || [],
          programacion_pago_orden_opcionales: this.editedSubtipo.programacion_pago_orden_opcionales || [],
          solicitud_pago_requisitos: this.editedSubtipo.solicitud_pago_requisitos || [],
          solicitud_pago_opcionales: this.editedSubtipo.solicitud_pago_opcionales || []
        }
      })
        .then(() => {
          this.closeDialogSubtipo();
          this.getItems();
        })
        .catch(error => {
          if (error.response && error.response.status === 409) {
            this.$refs.alert.setMessage(
              "",
              "Ya existe una subclasificación con ese nombre para este tipo de gasto."
            );
            this.$refs.alert.open();
            return;
          }
          console.log(error);
        })
        .finally(() => (this.loadingSaveSubtipo = false));
    },
    openDialogDeleteSubtipo(editItem) {
      this.editedSubtipo = Object.assign({}, this.defaultEditedSubtipo, editItem);
      this.dialogDeleteSubtipo = true;
    },
    closeDialogDeleteSubtipo() {
      this.editedSubtipo = Object.assign({}, this.defaultEditedSubtipo);
      this.dialogDeleteSubtipo = false;
    },
    deleteSubtipo() {
      this.loadingDeleteSubtipo = true;
      axios
        .delete(`/Pagos/EliminarGastoSubtipo/${this.editedSubtipo.id_gasto_subtipo}`)
        .then(() => {
          this.closeDialogDeleteSubtipo();
          this.getItems();
        })
        .catch(err => console.log(err))
        .finally(() => (this.loadingDeleteSubtipo = false));
    },
    filterOut(items, excludedValues) {
      const excluded = new Set(Array.isArray(excludedValues) ? excludedValues : []);
      return (items || []).filter(x => !excluded.has(x.value));
    },

    // asegura unicidad y además quita lo que exista en el "other"
    normalizePair(primaryArr, otherArr) {
      const primary = Array.isArray(primaryArr) ? primaryArr : [];
      const other = new Set(Array.isArray(otherArr) ? otherArr : []);
      // 1) unique 2) remove any that are in other
      return [...new Set(primary)].filter(v => !other.has(v));
    }
  }
};
</script>

<style scoped>
.rounded-lg {
  border-radius: 14px;
}

/* Master card sutil */
.card-master {
  background: linear-gradient(180deg, rgba(25,118,210,0.06), rgba(25,118,210,0.02));
}

/* selección en lista */
.selected-row {
  background: rgba(25, 118, 210, 0.08);
  border-left: 4px solid rgba(25, 118, 210, 0.8);
}

/* fila subtipo */
.subtipo-row {
  border-radius: 10px;
  margin-bottom: 6px;
}

/* Secciones OC / SP */
.section-card {
  border-radius: 14px;
  padding: 14px;
  border: 1px solid rgba(0,0,0,0.08);
}

.section-title {
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

/* OC: azul */
.section-card.oc {
  background: linear-gradient(180deg, rgba(33,150,243,0.10), rgba(33,150,243,0.03));
  border-color: rgba(33,150,243,0.25);
}

/* SP: verde */
.section-card.sp {
  background: linear-gradient(180deg, rgba(76,175,80,0.10), rgba(76,175,80,0.03));
  border-color: rgba(76,175,80,0.25);
}
</style>
